import React from 'react';

type Props = React.SVGProps<SVGSVGElement> & { size?: number };

const make = (path: React.ReactNode, viewBox = '0 0 24 24') =>
  function IconCmp({ size = 16, ...rest }: Props) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        {path}
      </svg>
    );
  };

export const Mail = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </>,
);

export const Inbox = make(
  <>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
  </>,
);

export const SendIcon = make(
  <>
    <path d="M22 2L11 13" />
    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
  </>,
);

export const Spam = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <circle cx="12" cy="16" r="0.6" fill="currentColor" />
  </>,
);

export const Reply = make(
  <>
    <path d="M9 17l-5-5 5-5" />
    <path d="M20 18v-2a4 4 0 00-4-4H4" />
  </>,
);

/** `Reply` mirrored horizontally (x → 24 − x, arc sweep flipped). */
export const Forward = make(
  <>
    <path d="M15 17l5-5-5-5" />
    <path d="M4 18v-2a4 4 0 014-4h12" />
  </>,
);

export const User = make(
  <>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20v-1a7 7 0 0114 0v1" />
  </>,
);

export const Plus = make(
  <>
    <path d="M12 5v14M5 12h14" />
  </>,
);

export const Bell = make(
  <>
    <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </>,
);

export const Search = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </>,
);

export const ChevronRight = make(<path d="M9 6l6 6-6 6" />);
export const ChevronDown = make(<path d="M6 9l6 6 6-6" />);
export const ChevronLeft = make(<path d="M15 18l-6-6 6-6" />);
export const ChevronsLeft = make(
  <>
    <path d="M11 17l-5-5 5-5" />
    <path d="M18 17l-5-5 5-5" />
  </>,
);
export const ChevronsRight = make(
  <>
    <path d="M13 17l5-5-5-5" />
    <path d="M6 17l5-5-5-5" />
  </>,
);
export const ChevronsUpDown = make(<path d="M8 9.5l4-4 4 4M8 14.5l4 4 4-4" />);
// Sidebar Help / Feedback / sign-out — drawn with the same rounded joins and
// radius-2 corners as the nav set (Mail, Bell, …). `HelpCircle` is separate
// from `Help` because `Help` is also the smaller inline "?" used in setup-score
// and DNS-status rows, whose look shouldn't change.
export const HelpCircle = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.2a2.5 2.5 0 014.8 1c0 1.7-2.4 2.3-2.4 2.3" />
    <path d="M12 16.5h.01" />
  </>,
);
export const LogOut = make(
  <>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </>,
);
export const MessageSquare = make(
  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
);
export const ArrowRight = make(
  <>
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </>,
);
export const ArrowUp = make(
  <>
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </>,
);
export const ArrowDown = make(
  <>
    <path d="M12 5v14" />
    <path d="M19 12l-7 7-7-7" />
  </>,
);
/** Up arrow beside a down arrow — "sortable, not sorted yet". */
export const ArrowUpDown = make(
  <>
    <path d="M7 4v16" />
    <path d="M4 7l3-3 3 3" />
    <path d="M17 20V4" />
    <path d="M14 17l3 3 3-3" />
  </>,
);

export const Check = make(<path d="M20 6L9 17l-5-5" />);
/** Double tick — "apply this to all". */
export const CheckCheck = make(<path d="M2 12.5l4.5 4.5L15 8.5M10 16l1 1 10-10.5" />);
export const X = make(
  <>
    <path d="M18 6L6 18M6 6l12 12" />
  </>,
);

export const MoreH = make(
  <>
    <circle cx="6" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="18" cy="12" r="1" fill="currentColor" stroke="none" />
  </>,
);

export const MoreV = make(
  <>
    {/* r=1.5, not the r=1 other filled dots in this file use: at the 20px
        render size RowActionsMenu passes, r=1 anti-aliases down to a
        near-invisible fleck (TRU-1059). */}
    <circle cx="12" cy="6" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18" r="1.5" fill="currentColor" stroke="none" />
  </>,
);

export const Refresh = make(
  <>
    <path d="M21 12a9 9 0 11-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </>,
);

export const Download = make(
  <>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </>,
);

export const Settings = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </>,
);

export const Users = make(
  <>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </>,
);

export const CreditCard = make(
  <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </>,
);

export const Layers = make(
  <>
    <path d="M12 2l10 6-10 6L2 8z" />
    <path d="M2 16l10 6 10-6" />
    <path d="M2 12l10 6 10-6" />
  </>,
);

export const FileText = make(
  <>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h8M8 9h2" />
  </>,
);

export const PieChart = make(
  <>
    <path d="M12 3v9h9" />
    <path d="M20.5 15.5A9 9 0 118.5 3.7" />
  </>,
);

export const BarChart = make(
  <>
    <path d="M12 20V10" />
    <path d="M18 20V4" />
    <path d="M6 20v-6" />
  </>,
);

/** Three list lines with a magnifier — the account page's Overview tab. */
export const ListSearch = make(
  <>
    <path d="M4 6h16" />
    <path d="M4 12h4" />
    <path d="M4 18h4" />
    <circle cx="15" cy="15" r="4" />
    <path d="M18 18l3 3" />
  </>,
);

/** A trend line over a baseline — the account page's Analytics tab. */
export const ChartTrend = make(
  <>
    <path d="M4 14.5l4.7-5.8 2.9 2 4.4-5.2 4 4.5" />
    <path d="M4 18.5h16" />
  </>,
);

export const Sparkle = make(
  <>
    <path d="M12 3v3" />
    <path d="M12 18v3" />
    <path d="M3 12h3" />
    <path d="M18 12h3" />
    <path d="M5.5 5.5l2 2" />
    <path d="M16.5 16.5l2 2" />
    <path d="M5.5 18.5l2-2" />
    <path d="M16.5 7.5l2-2" />
  </>,
);

export const Pause = make(
  <>
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </>,
);

export const Play = make(<path d="M6 4l14 8-14 8z" />);

export const Clock = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);

export const Flame = make(
  <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" />,
);

export const CheckCircle = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </>,
);

export const PauseCircle = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9v6M14 9v6" />
  </>,
);

export const Fire = make(
  <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" />,
);

export const AlertTriangle = make(
  <>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <path d="M12 9v4" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" />
  </>,
);

export const AlertCircle = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5" />
    <circle cx="12" cy="16" r="0.6" fill="currentColor" />
  </>,
);

export const Info = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r="0.6" fill="currentColor" />
  </>,
);

export const AlertOctagon = make(
  <>
    <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2z" />
    <path d="M12 8v5" />
    <circle cx="12" cy="16" r="0.6" fill="currentColor" />
  </>,
);

/**
 * Hexagon alert with a shield-check badge — "flagged as spam, and we verified
 * it". Used by the Spam tile in the Analytics Placement Breakdown.
 *
 * The hexagon is drawn as an OPEN path: it runs to its lower-right vertex and
 * stops, omitting only the lower-right edge. The badge tucks into that gap and
 * visually closes the shape, so the outline reads as whole without two 1.75px
 * strokes overlapping (which smudges at 18px).
 */
export const ShieldAlertHex = make(
  <>
    <path d="M9 16.4 L2.2 12.7 V5.3 L9 1.6 L15.8 5.3 V12.7" />
    <path d="M9 5.6v4" />
    <circle cx="9" cy="12.2" r="0.6" fill="currentColor" />
    <path d="M16.7 21.8s4.3-2 4.3-5.2v-3.2L16.7 12.1l-4.3 1.3v3.2c0 3.2 4.3 5.2 4.3 5.2z" />
    <path d="M14.8 16.8l1.4 1.4 2.5-2.7" />
  </>,
);

export const ShieldCheck = make(
  <>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </>,
);

export const Zap = make(<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />);

export const ExternalLink = make(
  <>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M18 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h5" />
  </>,
);

export const Globe = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13 13 0 010 18" />
    <path d="M12 3a13 13 0 000 18" />
  </>,
);

export const Filter = make(<path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />);

export const Calendar = make(
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </>,
);

export const Columns = make(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M12 4v16" />
  </>,
);

export const GripVertical = make(
  <>
    <circle cx="9" cy="6" r="0.6" fill="currentColor" />
    <circle cx="9" cy="12" r="0.6" fill="currentColor" />
    <circle cx="9" cy="18" r="0.6" fill="currentColor" />
    <circle cx="15" cy="6" r="0.6" fill="currentColor" />
    <circle cx="15" cy="12" r="0.6" fill="currentColor" />
    <circle cx="15" cy="18" r="0.6" fill="currentColor" />
  </>,
);

export const Lock = make(
  <>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </>,
);

export const Help = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" />
  </>,
);

export const ArrowDLeft = make(
  <>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <path d="M16 17l-5-5 5-5" />
    <path d="M21 12H11" />
  </>,
);

export const Copy = make(
  <>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </>,
);

export const Trash = make(
  <>
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </>,
);

export const Pencil = make(
  <>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
  </>,
);

export const Tag = make(
  <>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <path d="M7 7h.01" />
  </>,
);

export const Home = make(
  <>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10a1 1 0 001 1h4v-7h4v7h4a1 1 0 001-1V10" />
  </>,
);

export const Activity = make(
  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
);

export const Gauge = make(
  <>
    <path d="m12 14 4-4" />
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </>,
);

export const TrendingDown = make(
  <>
    <path d="M22 17l-8.5-8.5-5 5L2 7" />
    <path d="M16 17h6v-6" />
  </>,
);

export const TrendingUp = make(
  <>
    <path d="M22 7l-8.5 8.5-5-5L2 17" />
    <path d="M16 7h6v6" />
  </>,
);

export const Building = make(
  <>
    <rect x="3" y="9" width="13" height="13" rx="1" />
    <path d="M8 22V5a2 2 0 012-2h6a2 2 0 012 2v3" />
    <line x1="7" y1="13" x2="7" y2="13" />
    <line x1="11" y1="13" x2="11" y2="13" />
    <line x1="7" y1="17" x2="7" y2="17" />
    <line x1="11" y1="17" x2="11" y2="17" />
  </>,
);

export const Sun = make(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </>,
);

export const Moon = make(
  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />,
);

export const Link = make(
  <>
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </>,
);

export const Unlink = make(
  <>
    <path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 00-.12-7.07 5.006 5.006 0 00-6.95 0l-1.72 1.71" />
    <path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 00.12 7.07 5.006 5.006 0 006.95 0l1.71-1.71" />
    <line x1="8" y1="2" x2="8" y2="5" />
    <line x1="2" y1="8" x2="5" y2="8" />
    <line x1="16" y1="19" x2="16" y2="22" />
    <line x1="19" y1="16" x2="22" y2="16" />
  </>,
);

export const Star = make(
  <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 21 12 17.77 6.2 21l1.3-7.35L3 9.27l6.1-1.01L12 2z" />,
);

export const Eye = make(
  <>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

export const EyeFilled = make(
  <>
    <path
      fill="currentColor"
      stroke="none"
      fillRule="evenodd"
      d="M12 4.1C5.6 4.1 1.4 8.6.1 12c1.3 3.4 5.5 7.9 11.9 7.9s10.6-4.5 11.9-7.9C22.6 8.6 18.4 4.1 12 4.1zm0 3.4a4.5 4.5 0 1 0 0 9 4.5 4.5 0 1 0 0-9z"
    />
    <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
  </>,
);

export const Code = make(
  <>
    <path d="m18 16 4-4-4-4" />
    <path d="m6 8-4 4 4 4" />
    <path d="m14.5 4-5 16" />
  </>,
);

export { ShieldCheck as Shield, AlertTriangle as Warn, MoreH as More };
